import * as yup from 'yup';

import {
  VALIDATION_CONSTRAINTS,
  VALIDATION_MESSAGES,
  VALIDATION_REGEX,
} from '@/shared/constants/validation.constants';

export const APPLICANT_EMAIL_INVALID = 'Please enter a valid email address.';
export const APPLICANT_EMAIL_UNIQUE = 'Applicant emails need to be unique';
export const APPLICANT_EMAIL_UNIQUE_IGNORING_CASE =
  'Applicant emails need to be unique (ignoring case)';

export const applicantEmailValidate = yup.string().email(APPLICANT_EMAIL_INVALID);

type ApplicantEmailRow = { email?: string; is_deleted?: boolean };

// Array-level case-insensitive uniqueness. Per-entry required/format rules stay
// on the email field. This is the invite-create check: one error on the array.
export const applicantsUniqueValidate = yup
  .array()
  .test('unique', APPLICANT_EMAIL_UNIQUE, function (values, { createError }) {
    const entries = values as ApplicantEmailRow[] | undefined;
    if (!entries?.length) {
      return true;
    }
    const emails = entries.map(({ email }) => (email ?? '').toLowerCase());
    const hasDuplicate = new Set(emails).size !== emails.length;
    return hasDuplicate
      ? createError({ path: 'applicants', message: APPLICANT_EMAIL_UNIQUE_IGNORING_CASE })
      : true;
  });

export const duplicateEmailRowIndexes = (entries: ApplicantEmailRow[]): number[] => {
  const seen = new Set<string>();
  const indexes: number[] = [];
  entries.forEach((row, index) => {
    if (row?.is_deleted) {
      return;
    }
    const email = (row?.email ?? '').trim().toLowerCase();
    if (!email) {
      return;
    }
    if (seen.has(email)) {
      indexes.push(index);
      return;
    }
    seen.add(email);
  });
  return indexes;
};

// Same uniqueness rule, attached to every row after the first copy of an email.
// Deleted rows and blank emails are skipped so a staged removal does not collide
// with itself. One returned error would stop at the first duplicate.
export const applicantEmailsUniqueByRowValidate = yup
  .array()
  .test('unique-by-row', APPLICANT_EMAIL_UNIQUE, (values, testContext) => {
    const indexes = duplicateEmailRowIndexes((values as ApplicantEmailRow[] | undefined) ?? []);
    if (indexes.length === 0) {
      return true;
    }
    return new yup.ValidationError(
      indexes.map((index) =>
        testContext.createError({
          path: `${testContext.path}.${index}.email`,
          message: APPLICANT_EMAIL_UNIQUE_IGNORING_CASE,
        }),
      ),
    );
  });

export const APPLICANT_EMAIL_REQUIRED = 'Applicant email is required';
export const APPLICANT_EMAIL_MUST_BE_UNIQUE = 'Each applicant must have a unique email address';

/**
 * One applicant row's email in the deal and renewal lease forms: required, valid, within the max
 * length, and unique (case-insensitive) across the form's `applicants` array.
 */
export const applicantEmailRowSchema = yup
  .string()
  .required(APPLICANT_EMAIL_REQUIRED)
  .matches(VALIDATION_REGEX.EMAIL, VALIDATION_MESSAGES.EMAIL)
  .max(
    VALIDATION_CONSTRAINTS.EMAIL_MAX_LENGTH,
    `Email must not exceed ${VALIDATION_CONSTRAINTS.EMAIL_MAX_LENGTH} characters`,
  )
  .test('unique-email', APPLICANT_EMAIL_MUST_BE_UNIQUE, (value, context) => {
    if (!value) {
      return true;
    }
    // `from[1]` is the form root: [0] is this row, [1] the object holding `applicants`.
    const root = context.from?.[1]?.value as { applicants?: Array<{ email?: string }> } | undefined;
    const applicants = root?.applicants;
    if (!applicants) {
      return true;
    }
    const email = value.toLowerCase();
    return applicants.filter((applicant) => applicant.email?.toLowerCase() === email).length <= 1;
  });
